import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface DisplayHeadingProps extends HTMLAttributes<HTMLHeadingElement> {
  level?: 1 | 2 | 3 | 4;
  balance?: boolean;
  children: ReactNode;
}

const SIZE: Record<NonNullable<DisplayHeadingProps["level"]>, string> = {
  1: "text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight",
  2: "text-3xl sm:text-4xl leading-tight",
  3: "text-2xl sm:text-3xl leading-snug",
  4: "text-xl sm:text-2xl leading-snug",
};

/**
 * Display heading — renders h1..h4 in the active theme's display font
 * (var(--display-font)). Serif under theme-journal / theme-classic,
 * sans under theme-dawn. Inherits ink colour.
 */
export function DisplayHeading({
  level = 2,
  balance,
  className,
  style,
  children,
  ...rest
}: DisplayHeadingProps) {
  const Tag = (`h${level}` as unknown) as "h1";
  return (
    <Tag
      className={cn(SIZE[level], balance && "text-balance", "text-ink", className)}
      style={{ fontFamily: "var(--display-font)", ...style }}
      {...rest}
    >
      {children}
    </Tag>
  );
}
